
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import type { Profile } from "@/types/profile";
import type { Review } from "@/types/review";
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export function useProfileData() {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchUserData = async (userId: string) => {
    try {
      console.log("Fetching profile data for user:", userId);
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url, link_one, link_two, verification_status, is_creative')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error("Profile fetch error:", profileError);
        throw profileError;
      }
      
      console.log("Profile data fetched successfully:", profileData);
      
      // Update auth metadata to sync username, display_name, and avatar
      await supabase.auth.updateUser({
        data: {
          username: profileData.username,
          display_name: profileData.display_name,
          avatar_url: profileData.avatar_url
        }
      });
      
      // Type assertion to handle the Supabase response
      setProfile(profileData as Profile);

      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (reviewsError) {
        console.error("Reviews fetch error:", reviewsError);
        throw reviewsError;
      }
      
      console.log("Reviews fetched successfully, count:", reviewsData?.length || 0);
      setReviews(reviewsData || []);
    } catch (error: any) {
      console.error('Error fetching profile data:', error);
      setError(error.message || "Failed to load profile data");
      toast.error("Failed to load profile data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log("Initializing auth in useProfileData");
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Auth session error:", error);
          throw error;
        }
        
        if (!session) {
          console.log("No session found, redirecting to auth");
          navigate("/auth");
          return;
        }
        
        console.log("Session found, user:", session.user.id);
        setUser(session.user);
      } catch (error: any) {
        console.error("Auth initialization error:", error);
        setError(error.message || "Authentication error");
        toast.error("Authentication error");
        navigate("/auth");
      }
    };

    initializeAuth();

    // Subscribe to auth changes
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);
      if (event === "SIGNED_OUT") {
        navigate("/auth");
        return;
      }
      if (session?.user) {
        setUser(session.user);
      }
    });

    // Type guard function to check if the payload is a valid profile
    const isValidProfile = (obj: any): obj is Profile => {
      return obj && typeof obj.id === 'string';
    };

    // Subscribe to profile changes - with secure context check
    let profileSubscription: ReturnType<typeof supabase.channel> | null = null;
    let pollingInterval: number | null = null;

    try {
      // Check if we're in a secure context where WebSockets are allowed
      const isSecureContext = window.isSecureContext;
      const protocol = window.location.protocol;
      
      // Only set up realtime if we're in a secure context or using a secure protocol
      if (isSecureContext || protocol === 'https:') {
        profileSubscription = supabase
          .channel('profile-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'profiles',
            },
            (payload: RealtimePostgresChangesPayload<{
              id: string;
              username: string;
              display_name: string | null;
              avatar_url: string | null;
              link_one: string | null;
              link_two: string | null;
              verification_status: 'none' | 'pending' | 'verified' | 'rejected' | 'verified_creative';
              is_creative: boolean;
            }>) => {
              if (isValidProfile(payload.new) && user && payload.new.id === user.id) {
                console.log("Profile updated via realtime:", payload.new);
                setProfile(payload.new as Profile);
              }
            }
          )
          .subscribe((status) => {
            // Log subscription issues but don't crash
            if (status !== 'SUBSCRIBED') {
              console.warn(`Profile realtime subscription status: ${status}`);
            }
          });
      } else {
        // If we're not in a secure context, use polling instead
        console.log("Using polling for profile updates in non-secure context");
        pollingInterval = window.setInterval(() => {
          if (user) {
            // Lighter polling that just checks for profile updates
            // Fix: Convert PromiseLike to Promise with .then() handling errors inline
            supabase
              .from('profiles')
              .select('id, username, display_name, avatar_url, link_one, link_two, verification_status, is_creative')
              .eq('id', user.id)
              .single()
              .then(
                ({ data }) => {
                  if (data && (!profile || JSON.stringify(data) !== JSON.stringify(profile))) {
                    console.log("Profile updated via polling:", data);
                    setProfile(data as Profile);
                  }
                },
                (err) => console.error("Profile polling error:", err)
              );
          }
        }, 30000); // Poll every 30 seconds
      }
    } catch (error) {
      console.error("Failed to set up realtime subscription:", error);
      // Fallback to polling if realtime fails
      pollingInterval = window.setInterval(() => {
        if (user) {
          // Fix: Convert PromiseLike to Promise with .then() handling errors inline
          supabase
            .from('profiles')
            .select('id, username, display_name, avatar_url, link_one, link_two, verification_status, is_creative')
            .eq('id', user.id)
            .single()
            .then(
              ({ data }) => {
                if (data && (!profile || JSON.stringify(data) !== JSON.stringify(profile))) {
                  console.log("Profile updated via polling fallback:", data);
                  setProfile(data as Profile);
                }
              },
              (err) => console.error("Profile polling fallback error:", err)
            );
        }
      }, 30000); // Poll every 30 seconds
    }

    // Cleanup subscriptions
    return () => {
      console.log("Cleaning up subscriptions in useProfileData");
      authSubscription.unsubscribe();
      
      if (profileSubscription) {
        profileSubscription.unsubscribe();
      }
      
      if (pollingInterval) {
        window.clearInterval(pollingInterval);
      }
    };
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    
    console.log("User state changed, fetching data for:", user.id);
    fetchUserData(user.id);
  }, [user]);

  return {
    user,
    profile,
    reviews,
    isLoading,
    error,
    setProfile
  };
}
